import React, { useState } from "react";
import { useCasos } from "../hooks/useCasos";
import { checkParasitePresence } from "../utils/investigadorUtils";

const MisCasos = ({ onEdit, showToast, initialSearch = "", activeProjectId }) => {
    const { casos, loading, deleteCaso } = useCasos(activeProjectId, initialSearch);
    const [searchTerm, setSearchTerm] = useState(initialSearch);
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
    const [parasito, setParasito] = useState("");
    const [resultado, setResultado] = useState("POSITIVO");

    const handleDelete = async (id) => {
        try {
            await deleteCaso(id);
            showToast("Caso de investigación eliminado correctamente", "success");
            setShowDeleteConfirm(null);
        } catch (err) {
            showToast("Error al eliminar caso.", "danger");
            setShowDeleteConfirm(null);
        }
    };

    const filtered = casos.filter(c => {
        const matchesSearch = c.paciente_nombre?.toLowerCase().includes(searchTerm.toLowerCase()) ||
            c.paciente_documento?.includes(searchTerm) ||
            (c.paciente_municipio && c.paciente_municipio.toLowerCase().includes(searchTerm.toLowerCase()));

        if (!matchesSearch) return false;

        if (parasito) {
            const isPos = checkParasitePresence(c.parasitos_detectados, parasito);
            if (resultado === "POSITIVO") return isPos;
            return !isPos;
        }

        return true;
    });

    return (
        <div className="animate__animated animate__fadeIn">
            <div className="researcher-toolbar justify-content-between flex-wrap">
                <div className="d-flex flex-wrap gap-3 align-items-center">
                    <div className="researcher-search-wrapper" style={{ minWidth: '300px' }}>
                        <i className="bi bi-search"></i>
                        <input
                            type="text"
                            className="researcher-search-input"
                            placeholder="Buscar por paciente o municipio..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>

                    <div className="d-flex gap-2">
                        <select className="form-select form-select-sm border-0 shadow-sm" style={{ width: '180px', borderRadius: '10px' }} value={parasito} onChange={e => setParasito(e.target.value)}>
                            <option value="">Filtro: Microorganismo</option>
                            <option value="Ascaris">Ascaris lumbricoides</option>
                            <option value="Trichuris">Trichuris trichiura</option>
                            <option value="Uncinarias">Uncinarias</option>
                        </select>

                        <select className="form-select form-select-sm border-0 shadow-sm" style={{ width: '130px', borderRadius: '10px' }} value={resultado} onChange={e => setResultado(e.target.value)} disabled={!parasito}>
                            <option value="POSITIVO">POSITIVO (+)</option>
                            <option value="NEGATIVO">NEGATIVO (-)</option>
                        </select>
                    </div>
                </div>
                {initialSearch && (
                    <div className="badge border border-success text-success p-2" style={{ background: '#f1f8e9', borderRadius: '12px' }}>
                        <i className="bi bi-person-badge-fill me-2"></i>
                        Historial: <strong>{initialSearch}</strong>
                    </div>
                )}
            </div>

            <div className="researcher-table-container">
                <div className="table-responsive">
                    <table className="researcher-table align-middle">
                        <thead>
                            <tr>
                                <th>Fecha Registro</th>
                                <th>Paciente / Sujeto</th>
                                <th>Microorganismos e Intensidad</th>
                                <th>Proyecto</th>
                                <th>Nivel de Riesgo</th>
                                <th>Municipio</th>
                                <th className="text-end">Operaciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr><td colSpan="7" className="text-center py-5">
                                    <div className="spinner-border spinner-border-sm text-success me-2"></div>
                                    <span className="text-muted fw-bold">Analizando registros epidemiológicos...</span>
                                </td></tr>
                            ) : filtered.length === 0 ? (
                                <tr><td colSpan="7" className="text-center py-5 text-muted fw-bold">No se encontraron casos clínicos bajo estos parámetros.</td></tr>
                            ) : (
                                filtered.map(c => (
                                    <tr key={c.id}>
                                        <td className="small text-muted fw-bold">{c.fecha_registro}</td>
                                        <td>
                                            <div className="fw-bold">{c.paciente_nombre || `Paciente #${c.paciente}`}</div>
                                            <div className="small text-muted" style={{ fontSize: '0.7rem' }}>Doc: {c.paciente_documento || '---'}</div>
                                        </td>
                                        <td>
                                            {c.parasitos_detectados && c.parasitos_detectados.length > 0 ? (
                                                <div className="d-flex flex-wrap gap-1">
                                                    {c.parasitos_detectados.map((p, i) => {
                                                        const hpg = c.intensidad_hpg?.[p] || 0;
                                                        return (
                                                            <div key={i} className="badge bg-light text-dark border p-2 d-flex align-items-center gap-2" style={{ borderRadius: '8px', fontSize: '0.7rem' }}>
                                                                <span className="fw-bold text-primary">{p}</span>
                                                                <span className="bg-white px-2 py-0 rounded border fw-bold">{hpg} HPG</span>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            ) : (
                                                <span className="badge bg-light text-muted border px-3 py-2" style={{ borderRadius: '8px' }}>NO DETECTADOS</span>
                                            )}
                                        </td>
                                        <td><span className="badge bg-light text-success border border-success" style={{ fontSize: '0.7rem' }}>{c.proyecto_codigo || c.codigo_proyecto_investigacion || c.paciente_codigo_proyecto_investigacion || "N/A"}</span></td>
                                        <td>
                                            <span className={`badge px-3 py-2 w-100 ${c.nivel_peligrosidad === 'ALTO' ? 'bg-danger shadow-sm' :
                                                c.nivel_peligrosidad === 'MEDIO' ? 'bg-warning text-dark shadow-sm' : 'bg-success shadow-sm'
                                                }`} style={{ borderRadius: '8px' }}>
                                                <i className={`bi bi-${c.nivel_peligrosidad === 'ALTO' ? 'exclamation-triangle-fill' : 'check-circle-fill'} me-1`}></i>
                                                {c.nivel_peligrosidad || "BAJO"}
                                            </span>
                                        </td>
                                        <td>{c.paciente_municipio || "---"}</td>
                                        <td className="text-end">
                                            <div className="d-flex justify-content-end gap-1">
                                                <button className="btn btn-sm btn-light text-success border" onClick={() => onEdit(c)} title="Editar Análisis">
                                                    <i className="bi bi-pencil-square"></i>
                                                </button>
                                                <button className="btn btn-sm btn-light text-danger border" onClick={() => setShowDeleteConfirm(c)} title="Eliminar Registro">
                                                    <i className="bi bi-trash"></i>
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {showDeleteConfirm && (
                    <div className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center" style={{ zIndex: 100, background: 'rgba(255,255,255,0.8)', backdropFilter: 'blur(4px)', borderRadius: '20px' }}>
                        <div className="card border-0 shadow-lg" style={{ maxWidth: '400px', borderRadius: '20px' }}>
                            <div className="card-body text-center p-4">
                                <div className="bg-danger bg-opacity-10 p-3 rounded-circle d-inline-block mb-3">
                                    <i className="bi bi-microscope-off text-danger fs-1"></i>
                                </div>
                                <h5 className="fw-bold text-dark">¿Eliminar Análisis Clínico?</h5>
                                <p className="text-muted small">
                                    Está a punto de borrar los resultados del conteo de huevos para <strong>{showDeleteConfirm.paciente_nombre}</strong>.
                                    Esta acción eliminará permanentemente la evidencia científica de este registro.
                                </p>
                                <div className="d-flex justify-content-center gap-2 mt-4">
                                    <button className="btn btn-light px-4 fw-bold" style={{ borderRadius: '10px' }} onClick={() => setShowDeleteConfirm(null)}>CANCELAR</button>
                                    <button className="btn btn-danger px-4 fw-bold" style={{ borderRadius: '10px' }} onClick={() => handleDelete(showDeleteConfirm.id)}>SÍ, ELIMINAR ANÁLISIS</button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default MisCasos;
